import config from '../config';
import { twitterPageURL } from './urlHelpers';

/**
 * Build hreflang alternates for a localized story from its translated slugs.
 *
 * Emits one `{ hrefLang, href }` per PUBLISHED language, using that language's OWN
 * translated slug (so `/it/blog/pedaggio-croazia`, not the German slug with the locale
 * swapped). The current locale is always included and points at `selfHref` (the page
 * being rendered). Unpublished translations are omitted on purpose — their localized
 * URLs do not exist yet and 404 by design, so we must not advertise them.
 *
 * @param {object} params
 * @param {Array<{lang: string, slug: string, published: boolean}>} [params.translatedSlugs]
 * @param {string} params.currentLocale - Locale of the page being rendered (always self).
 * @param {string} params.selfHref - Canonical URL of the current page.
 * @param {string} params.canonicalRootURL - Site root, e.g. `https://vansite.eu`.
 * @returns {Array<{hrefLang: string, href: string}>}
 */
export const localizedAlternateLinks = ({
  translatedSlugs,
  currentLocale,
  selfHref,
  canonicalRootURL,
}) => {
  const byLang = new Map();

  // Current locale first; it is self-referential and always published (we render it).
  if (currentLocale) {
    byLang.set(currentLocale, selfHref);
  }

  (translatedSlugs || [])
    .filter((entry) => entry && entry.published && entry.lang && entry.slug)
    .forEach((entry) => {
      // Never let a translated_slugs row override the current locale's self href.
      if (entry.lang === currentLocale) {
        return;
      }
      const slug = entry.slug.replace(/^\/+|\/+$/g, '');
      byLang.set(entry.lang, `${canonicalRootURL}/${entry.lang}/${slug}`);
    });

  return [...byLang.entries()].map(([hrefLang, href]) => ({ hrefLang, href }));
};

const ensureOpenGraphLocale = (locale) => {
  switch (locale) {
    case 'en':
      return 'en_US';
    default:
      return locale;
  }
};

/**
 * These will be used with Helmet <meta {...openGraphMetaProps} />
 * @param {object} data - The data object containing the necessary properties.
 * @param {string} data.canonicalRootURL - The canonical root URL.
 * @param {string} data.contentType - The content type.
 * @param {string} data.description - The description.
 * @param {string} data.facebookAppId - The Facebook app ID.
 * @param {Array} data.facebookImages - The Facebook images.
 * @param {string} data.locale - The locale.
 * @param {string} data.published - The published date.
 * @param {string} data.siteTitle - The site title.
 * @param {Array} data.tags - The tags.
 * @param {string} data.title - The title.
 * @param {string} data.updated - The updated date.
 * @param {string} data.url - The URL.
 * @returns {Array} - The Open Graph meta tags.
 */
export const openGraphMetaProps = (data) => {
  const {
    canonicalRootURL,
    contentType,
    description,
    facebookAppId,
    facebookImages,
    locale,
    published,
    siteTitle,
    tags,
    title,
    updated,
    url,
  } = data;

  if (!(title && description && contentType && url && facebookImages && canonicalRootURL)) {

    if (console && console.warn) {
      console.warn(
        `Can't create Open Graph meta tags:
        title, description, contentType, url, facebookImages, and canonicalRootURL are needed.`,
      );
    }

    return [];
  }

  const openGraphMeta = [
    { property: 'og:description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:type', content: contentType },
    { property: 'og:url', content: url },
    { property: 'og:locale', content: ensureOpenGraphLocale(locale) },
  ];

  if (facebookImages && facebookImages.length > 0) {
    facebookImages.forEach((i) => {
      openGraphMeta.push({
        property: 'og:image',
        content: i.url,
      });

      if (i.width && i.height) {
        openGraphMeta.push({ property: 'og:image:width', content: i.width });
        openGraphMeta.push({ property: 'og:image:height', content: i.height });
      }
    });
  }

  if (siteTitle) {
    openGraphMeta.push({ property: 'og:site_name', content: siteTitle });
  }

  if (facebookAppId) {
    openGraphMeta.push({ property: 'fb:app_id', content: facebookAppId });
  }

  if (published) {
    openGraphMeta.push({ property: 'article:published_time', content: published });
  }

  if (updated) {
    openGraphMeta.push({ property: 'article:modified_time', content: updated });
  }

  if (tags) {
    openGraphMeta.push({ property: 'article:tag', content: tags });
  }

  return openGraphMeta;
};

/**
 * These will be used with Helmet <meta {...twitterMetaProps} />
 * @param {object} data - The data object containing the necessary properties.
 * @param {string} data.canonicalRootURL - The canonical root URL.
 * @param {string} data.description - The description.
 * @param {string} data.siteTwitterHandle - The site Twitter handle.
 * @param {string} data.title - The title.
 * @param {string} data.twitterHandle - The Twitter handle.
 * @param {Array} data.twitterImages - The Twitter images.
 * @param {string} data.url - The URL.
 * @returns {Array} - The Twitter meta tags.
 */
export const twitterMetaProps = (data) => {
  const {
    canonicalRootURL,
    description,
    siteTwitterHandle,
    title,
    twitterHandle,
    twitterImages,
    url,
  } = data;

  if (!(title && description && siteTwitterHandle && url)) {

    if (console && console.warn) {
      console.warn(
        `Can't create twitter card meta tags:
        title, description, siteTwitterHandle, and url are needed.`,
      );
    }

    return [];
  }

  const twitterMeta = [
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:site', content: siteTwitterHandle },
    { name: 'twitter:url', content: url },
  ];

  if (canonicalRootURL && twitterImages && twitterImages.length > 0) {
    twitterImages.forEach((i) => {
      twitterMeta.push({
        name: 'twitter:image',
        content: i.url,
      });
    });
  }

  if (twitterHandle) {
    // TODO: If we want to connect providers twitter account on ListingPage
    // we needs to get this info among listing data (API support needed)
    twitterMeta.push({ name: 'twitter:creator', content: twitterHandle });
  }

  if (canonicalRootURL) {
    twitterMeta.push({ name: 'twitter:domain', content: canonicalRootURL });
  }

  return twitterMeta;
};

/**
 * These will be used with Helmet <meta {...metaTagProps} />
 * Creates data for Open Graph and Twitter meta tags.
 * @param {object} tagData - The data object containing the necessary properties.
 * @param {string} tagData.author - The author.
 * @param {string} tagData.description - The description.
 * @param {string} tagData.siteTitle - The site title.
 * @param {string} tagData.siteTwitterHandle - The site Twitter handle.
 * @returns {Array} - The meta tags.
 */
export const metaTagProps = (tagData) => {
  const { canonicalRootURL, facebookAppId, siteTitle, siteTwitterHandle } = config;

  const author = tagData.author || siteTitle;
  const defaultMeta = [
    { name: 'description', content: tagData.description },
    { name: 'author', content: author },
  ];

  const openGraphMeta = openGraphMetaProps({
    ...tagData,
    canonicalRootURL,
    facebookAppId,
    siteTitle,
  });

  const twitterMeta = twitterMetaProps({
    ...tagData,
    canonicalRootURL,
    siteTwitterHandle,
  });

  return [...defaultMeta, ...openGraphMeta, ...twitterMeta];
};

/**
 * Generates JSON-LD schema markup for the page.
 * @param {string} schemaTitle - The title of the schema.
 * @param {string} schemaDescription - The description of the schema.
 * @param {Object|Array|null} [schema={}] - Additional schema markup to be included.
 * @returns {string} JSON-LD schema markup as a string with escaped characters for HTML safety.
 */
/**
 * Remove lone (unpaired) UTF-16 surrogates from a string.
 *
 * Free user text (listing descriptions, review bodies, display names) sometimes
 * arrives with half an emoji — one surrogate of a pair — because it was truncated
 * mid-character upstream (a maxLength cut, a client splitting a surrogate pair).
 * `JSON.stringify` faithfully escapes such a lone surrogate to `\uD83D`, and Google's
 * Rich Results parser then rejects the whole item with "Truncated Unicode character",
 * disqualifying it from rich results. Stripping the orphan halves keeps the JSON-LD
 * valid regardless of what garbage is stored.
 *
 * @param {string} str
 * @returns {string}
 */
export const stripLoneSurrogates = (str) =>
  String(str)
    // high surrogate NOT followed by a low surrogate
    .replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])/g, '')
    // low surrogate NOT preceded by a high surrogate
    .replace(/(^|[^\uD800-\uDBFF])[\uDC00-\uDFFF]/g, '$1');

export const generatePageSchema = (schemaTitle, schemaDescription, schema = {}) => {
  const canonicalRootURL = config.canonicalRootURL;
  const siteTitle = config.siteTitle;

  const facebookPage = config.siteFacebookPage;
  const twitterPage = twitterPageURL(config.siteTwitterHandle);
  const instagramPage = config.siteInstagramPage;
  const sameOrganizationAs = [facebookPage, twitterPage, instagramPage].filter((v) => v != null);

  const schemaFromProps = schema === null ? [] : Array.isArray(schema) ? schema : [schema];
  const schemaArrayJSONString = JSON.stringify(
    [
      ...schemaFromProps,
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${canonicalRootURL}#organization`,
        url: canonicalRootURL,
        name: siteTitle,
        sameAs: sameOrganizationAs,
        logo: `${canonicalRootURL}/static/webapp-icon-192x192.png`,
        address: config.address,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        url: canonicalRootURL,
        description: schemaDescription,
        name: schemaTitle,
        publisher: {
          '@id': `${canonicalRootURL}#organization`,
        },
      },
    ],
    // Strip lone surrogates from every string value so a truncated emoji in user
    // text can't produce an invalid `\uD83D` in the JSON-LD (Google Rich Results
    // "Truncated Unicode character"). See stripLoneSurrogates above.
    (key, value) => (typeof value === 'string' ? stripLoneSurrogates(value) : value)
  );

  return schemaArrayJSONString.replace(/</g, '\\u003c');
};